import { logger } from '@fe/core';
import {
    GDFHotPollyShape,
    GDFHotPollyTypes,
    GDFPalletCode,
    GDFRecordType,
    GDFSubRecordType,
    hasTooltip,
    HotPolyTitle,
    isRaimProduct,
    isTracksProduct,
    WeatherPickingInfoLayer,
    WeatherPickingInfoLayerHoverClear,
    WeatherPickingInfoObject,
    WeatherTooltip
} from '@fe/weather';
import { SVGIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { EventBus } from '../../../app/contexts/event-bus';
import { useAppDispatch } from '../../../app/hooks/store-hooks';
import { toggleLeftFlyout } from '../../../app/state/slices/workspace-slice';
import { patchFocusedWorkspaceView } from '../../../app/state/store';
import useIntegration, { IntegrationInfo } from '../../../hooks/use-integration';
import useMapInteraction from '../../../hooks/use-map-interaction';
import {
    AirportTowerOutline,
    CogOutline,
    CommentOutline,
    DrawingPolygonOutline,
    DrawingPolylineOutline,
    DuplicateOutline,
    FilterOutline
} from '../../../icons/icons';
import { WeatherContextMenuData } from '../../../services/map-interaction-service';
import {
    UDViewDestinationTypeEnum,
    UDViewFlightFilterObjectEnum,
    UDViewPlaneFilterAirlineTypeEnum,
    UDViewTagTextColor
} from '../../../udView/udview';
import { Menu, MenuItem } from '../../common/menu/DropdownMenu';
import { FPMFillColor, FPMLineColor } from '../../drawing-object/DrawingObjectEditWindow';
import { DrawingObjectCategory, DrawingObjectFillStyle, DrawingObjectType } from '../../drawing-object/types';
import useConvertToText from '../../hooks/use-convert-to-text';

// Type aliases for picked layer and objects that attach options array for context menu options for each object type in each layer
type WeatherDisplayObject = WeatherPickingInfoObject & {
    options: ContextMenuOption[];
};

type WeatherDisplayLayer = Omit<WeatherPickingInfoLayer, 'objects'> & {
    objects: WeatherDisplayObject[];
};

export type WeatherDetailsWindowArgs = {
    layer: WeatherPickingInfoLayer;
    object: WeatherPickingInfoObject;
};

export function WeatherContextMenu(props: { info: WeatherContextMenuData }) {
    const context = props.info;
    const interactionService = useMapInteraction();
    const integrationInfo = useIntegration();
    const clearHoverFunction = React.useRef<WeatherPickingInfoLayerHoverClear | null | undefined>(null);
    const dispatch = useAppDispatch();
    const convertToText = useConvertToText();

    const handleMouseIn = React.useCallback((layer: WeatherPickingInfoLayer, item: WeatherPickingInfoObject) => {
        clearHoverFunction.current = layer.hover?.setHover(item);
    }, []);

    const clearHover = React.useCallback(() => {
        clearHoverFunction?.current?.clear();
        clearHoverFunction.current = null;
    }, []);

    const extractCoords = (menuRecord: WeatherPickingInfoObject) => {
        if (menuRecord.type === 'raster') {
            return null;
        }

        if (menuRecord.type === 'subRecord') {
            if (menuRecord.record.type === GDFSubRecordType.wmoAshCloud2) {
                return menuRecord.record.points;
            }
            return null;
        }

        const coords: number[][] = [];
        if (
            (menuRecord.record.type === GDFRecordType.hotpolyhi || menuRecord.record.type === GDFRecordType.hotpoly) &&
            menuRecord.record.reduced
        ) {
            const points = menuRecord.record.points;
            for (let i = 0; i < points.length; i += 2) {
                coords.push([points[i], points[i + 1]]);
            }
        } else if (
            (menuRecord.record.type === GDFRecordType.ccfp ||
                menuRecord.record.type === GDFRecordType.hotpolyhi ||
                menuRecord.record.type === GDFRecordType.hotpoly ||
                menuRecord.record.type === GDFRecordType.fca) &&
            menuRecord.record.points.length >= 4
        ) {
            for (const point of menuRecord.record.points) {
                coords.push([point[0], point[1]]);
            }
        }
        const shape: GDFHotPollyShape | undefined =
            menuRecord && menuRecord.record && 'shape' in menuRecord.record ? (menuRecord.record as any).shape : undefined;

        // If line we duplicate the points in reverse order with a very small offset to make it a (very thin) polygon
        if (shape && shape === GDFHotPollyShape.line && coords.length >= 2) {
            const firstPoint = coords[0];
            const lastPoint = coords[coords.length - 1];

            if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
                const offset = 0.0001; // Very small offset in degrees
                const reversedCoords = [...coords].reverse().map((point) => [point[0] + offset, point[1] + offset]);
                coords.push(...reversedCoords);
            }
            coords.push(coords[0]); // close
        }
        return coords;
    };

    const handleOnClick = async (layer: WeatherPickingInfoLayer, object: WeatherPickingInfoObject, option: ContextMenuOption) => {
        clearHover();
        interactionService.dismissContextMenu();
        if (option === ContextMenuOption.properties) {
            EventBus.fire('weather-details-window-open', { layer: layer, object: object });
        } else if (option === ContextMenuOption.copyToClipboard) {
            if (object.type === 'record') {
                if (object.record.type === GDFRecordType.metar || object.record.type === GDFRecordType.taf) {
                    if (object.record.text) {
                        await navigator.clipboard.writeText(object.record.text);
                    }
                } else {
                    try {
                        const text = await convertToText(layer, object);
                        navigator.clipboard.writeText(text).then();
                    } catch (error) {
                        if (error instanceof Error) {
                            logger.trackException(error);
                        }
                    }
                }
            }
        } else if (option === ContextMenuOption.airportSummary) {
            if (object.type === 'record') {
                let airportId = '';
                if (object.record.type === GDFRecordType.metar || object.record.type === GDFRecordType.taf) {
                    airportId = object.record.id;
                } else if (
                    object.record.type === GDFRecordType.delay ||
                    object.record.type === GDFRecordType.rvr ||
                    object.record.type === GDFRecordType.diversion
                ) {
                    airportId = object.record.airportId;
                }
                if (airportId) {
                    dispatch(toggleLeftFlyout({ type: 'airport', airportId: airportId }));
                }
            }
        } else if (option === ContextMenuOption.setPlaneFilter) {
            if (object.type === 'record') {
                if (object.record.type === GDFRecordType.diversion) {
                    const fromAcids = object.record.elements.filter((x) => x.active && x.type === 'F').map((x) => x.acid);
                    const toAcids = object.record.elements.filter((x) => x.active && x.type === 'T').map((x) => x.acid);

                    dispatch(
                        patchFocusedWorkspaceView((x) => {
                            x.plane.filter.planeFilterSettings.forEach((x) => (x.isActive = false));
                            x.plane.filter.useFilters = true;
                            fromAcids.forEach((acid) =>
                                x.plane.filter.planeFilterSettings.push({
                                    name: acid,
                                    color: '#ff0000',
                                    isActive: true,
                                    include: false,
                                    airlineType: UDViewPlaneFilterAirlineTypeEnum.SpecificAirlineId,
                                    useMyFleet: false,
                                    acid: acid,
                                    origin: null,
                                    destination: null,
                                    destinationType: UDViewDestinationTypeEnum.OriginToDestination,
                                    useAltAirport: false,
                                    beaconCode: null,
                                    icaoCode: null,
                                    altitudeLower: null,
                                    altitudeUpper: null,
                                    speedLower: null,
                                    speedUpper: null,
                                    aircraftType: null,
                                    filterObjectType: UDViewFlightFilterObjectEnum.Aircraft,
                                    timeBefore: null,
                                    timeAfter: null,
                                    timeType: null,
                                    distanceToDestination: null,
                                    sectorDeskId: null,
                                    tail: null,
                                    diverted: false,
                                    holding: false,
                                    showTags: false,
                                    showHistory: false,
                                    showHolding: false,
                                    showDiverting: false,
                                    tagTextColor: UDViewTagTextColor.default
                                })
                            );
                            toAcids.forEach((acid) =>
                                x.plane.filter.planeFilterSettings.push({
                                    name: acid,
                                    color: '#0000ff',
                                    isActive: true,
                                    include: false,
                                    airlineType: UDViewPlaneFilterAirlineTypeEnum.SpecificAirlineId,
                                    useMyFleet: false,
                                    acid: acid,
                                    origin: null,
                                    destination: null,
                                    destinationType: UDViewDestinationTypeEnum.OriginToDestination,
                                    useAltAirport: false,
                                    beaconCode: null,
                                    icaoCode: null,
                                    altitudeLower: null,
                                    altitudeUpper: null,
                                    speedLower: null,
                                    speedUpper: null,
                                    aircraftType: null,
                                    filterObjectType: UDViewFlightFilterObjectEnum.Aircraft,
                                    timeBefore: null,
                                    timeAfter: null,
                                    timeType: null,
                                    distanceToDestination: null,
                                    sectorDeskId: null,
                                    tail: null,
                                    diverted: false,
                                    holding: false,
                                    showTags: false,
                                    showHistory: false,
                                    showHolding: false,
                                    showDiverting: false,
                                    tagTextColor: UDViewTagTextColor.default
                                })
                            );
                        })
                    );
                }
            }
        } else if (option === ContextMenuOption.createDrawingObject) {
            const coords = extractCoords(object);
            if (!coords) {
                return;
            }

            if (coords.length <= 3) {
                console.warn('No enough points to create drawing object');
                return;
            }
            EventBus.fire('draw.create', {
                drawObject: {
                    type: DrawingObjectType.polygon,
                    points: coords
                }
            });
        } else if (option === ContextMenuOption.createFPMAvoidanceArea) {
            const coords = extractCoords(object);
            if (!coords) {
                return;
            }
            if (coords.length <= 3) {
                console.warn('Not enough points to create drawing object');
                return;
            }
            const now = dayjs();
            EventBus.fire('draw.create', {
                drawObject: {
                    name: `Avoidance_${now.utc().format('YYYYMMDD_HHmmss')}Z`,
                    label: {
                        text: `Avoidance_${now.utc().format('YYYYMMDD_HHmmss')}Z`
                    },
                    type: DrawingObjectType.polygon,
                    points: coords,
                    category: DrawingObjectCategory.avoidance,
                    fpmProperties: {
                        sync: true
                    },
                    line: {
                        color: FPMLineColor
                    },
                    fill: {
                        color: FPMFillColor,
                        style: DrawingObjectFillStyle.diagHatchLeft
                    }
                }
            });
        }
    };

    // Go through all layers and all objects inside those layers and attach options property to each weather object.
    // Then filter out object that have no context options and then filter out layers that have no objects.
    // Need to do this before hand because if all layers and all objects have no context options we will render an empty context menu.
    const displayData: WeatherDisplayLayer[] = useMemo(
        () =>
            context.layers
                .map((layer) => ({
                    ...layer,
                    objects: layer.objects
                        .map((object) => ({ ...object, options: getOptions(layer, object, integrationInfo) }))
                        // Get rid of objects with no context options
                        .filter((x) => x.options.length > 0)
                }))
                // Get rid of layers with no objects
                .filter((layers) => layers.objects.length > 0) ?? [],
        [context, integrationInfo]
    );

    return (
        <div style={{ minWidth: 120, maxWidth: 250, backgroundColor: 'light-dark(var(--color-neutral-900), var(--color-neutral-100))' }}>
            {displayData.map((layer) => (
                <div key={layer.product.id} style={{ fontSize: 12 }} className="d-flex flex-column">
                    <div className="px-2 pt-1 d-flex gap-1 align-items-center fw-bold">{layer.product.name}</div>
                    <WeatherContextMenuLayer
                        layer={layer}
                        onClick={handleOnClick}
                        onMouseIn={handleMouseIn}
                        onMouseOut={clearHover}
                        integrationInfo={integrationInfo}
                    />
                </div>
            ))}
        </div>
    );
}

function WeatherContextMenuLayer(props: {
    layer: WeatherDisplayLayer;
    onClick: (layer: WeatherPickingInfoLayer, object: WeatherPickingInfoObject, option: ContextMenuOption) => void;
    onMouseOut: () => void;
    onMouseIn: (layer: WeatherPickingInfoLayer, item: WeatherPickingInfoObject) => void;
    integrationInfo: IntegrationInfo;
}) {
    return (
        <>
            {props.layer.objects.length === 1 ? (
                <WeatherContextMenuLayerOptions
                    layer={props.layer}
                    object={props.layer.objects[0]}
                    onClick={props.onClick}
                    onMouseOut={props.onMouseOut}
                    onMouseIn={props.onMouseIn}
                    integrationInfo={props.integrationInfo}
                />
            ) : (
                props.layer.objects.map((object, i) => (
                    <Menu
                        label={objectToDisplayName(props.layer, object)}
                        key={i}
                        onFocusInOrMouseLeave={() => props.onMouseIn(props.layer, object)}
                        onFocusOutOrMouseOut={() => props.onMouseOut()}>
                        <WeatherContextMenuLayerOptions
                            layer={props.layer}
                            object={object}
                            onClick={props.onClick}
                            onMouseOut={props.onMouseOut}
                            onMouseIn={props.onMouseIn}
                            integrationInfo={props.integrationInfo}
                        />
                    </Menu>
                ))
            )}
        </>
    );
}

export function WeatherContextMenuLayerOptions(props: {
    layer: WeatherDisplayLayer;
    object: WeatherDisplayObject;
    onClick: (layer: WeatherPickingInfoLayer, object: WeatherPickingInfoObject, option: ContextMenuOption) => void;
    onMouseOut: () => void;
    onMouseIn: (layer: WeatherPickingInfoLayer, item: WeatherPickingInfoObject) => void;
    integrationInfo: IntegrationInfo;
}) {
    return props.object.options.map((optionId) =>
        optionId === ContextMenuOption.details ? (
            <Menu
                key={optionId}
                label="Details"
                icon={CommentOutline}
                onMouseEnter={() => props.onMouseIn(props.layer, props.object)}
                onMouseLeave={() => props.onMouseOut()}>
                <div className="p-2" style={{ maxWidth: 300 }}>
                    <WeatherTooltip layer={props.layer} object={props.object} />
                </div>
            </Menu>
        ) : (
            <MenuItem
                key={optionId}
                onClick={() => props.onClick(props.layer, props.object, optionId)}
                label={menuOptions[optionId].name}
                icon={menuOptions[optionId].icon}
                onMouseEnter={() => props.onMouseIn(props.layer, props.object)}
                onMouseLeave={props.onMouseOut}
                disabled={
                    optionId === ContextMenuOption.createFPMAvoidanceArea && !props.integrationInfo.isActionEnabled('canSendDrawObjToFPM')
                }
                hint={
                    optionId === ContextMenuOption.createFPMAvoidanceArea
                        ? props.integrationInfo.getDisabledReason('canSendDrawObjToFPM')
                        : null
                }
            />
        )
    );
}

export interface ContextMenuLayerObject {
    object: WeatherPickingInfoObject;
    disabled?: boolean;
    options: ContextMenuOption[];
}

enum ContextMenuOption {
    properties,
    copyToClipboard,
    createDrawingObject,
    airportSummary,
    setPlaneFilter,
    createFPMAvoidanceArea,
    details
}

const menuOptions: Record<ContextMenuOption, { name: string; icon?: SVGIcon }> = {
    [ContextMenuOption.properties]: { name: 'Properties', icon: CogOutline },
    [ContextMenuOption.copyToClipboard]: { name: 'Copy to clipboard', icon: DuplicateOutline },
    [ContextMenuOption.createDrawingObject]: { name: 'Create drawing object', icon: DrawingPolygonOutline },
    [ContextMenuOption.airportSummary]: { name: 'Airport Summary', icon: AirportTowerOutline },
    [ContextMenuOption.setPlaneFilter]: { name: 'Set plane filter to show diversions', icon: FilterOutline },
    [ContextMenuOption.createFPMAvoidanceArea]: { name: 'Create FPM Avoidance area ', icon: DrawingPolylineOutline },
    [ContextMenuOption.details]: { name: 'Details', icon: CommentOutline }
};

const contextMenuOptionsList: Partial<Record<GDFRecordType, ContextMenuOption[]>> = {
    [GDFRecordType.ccfp]: [ContextMenuOption.properties, ContextMenuOption.createDrawingObject],
    [GDFRecordType.hotpoly]: [ContextMenuOption.createDrawingObject],
    [GDFRecordType.hotpolyhi]: [ContextMenuOption.createDrawingObject],
    [GDFRecordType.delay]: [ContextMenuOption.airportSummary],
    [GDFRecordType.taf]: [ContextMenuOption.airportSummary, ContextMenuOption.copyToClipboard],
    [GDFRecordType.metar]: [ContextMenuOption.airportSummary, ContextMenuOption.copyToClipboard],
    [GDFRecordType.rvr]: [ContextMenuOption.airportSummary],
    [GDFRecordType.diversion]: [ContextMenuOption.properties, ContextMenuOption.airportSummary, ContextMenuOption.setPlaneFilter],
    [GDFRecordType.fca]: [ContextMenuOption.createDrawingObject, ContextMenuOption.copyToClipboard, ContextMenuOption.properties],
    [GDFRecordType.iataturb]: [ContextMenuOption.copyToClipboard],
    [GDFRecordType.wmovolcano]: [ContextMenuOption.properties],
    [GDFRecordType.wmovolcano2]: [ContextMenuOption.properties]
};

function getOptions(
    layer: WeatherPickingInfoLayer,
    object: WeatherPickingInfoObject,
    integrationInfo: IntegrationInfo
): ContextMenuOption[] {
    // TODO: I don't think we should base context menu on platte id, just either on record type or product id
    // context menu depends sometimes on palette code, sometimes on product id, and sometimes on record type
    // because why not?
    const options = [] as ContextMenuOption[];
    const hasDetails = hasTooltip(layer, object);

    if (hasDetails) {
        options.push(ContextMenuOption.details);
    }

    if (object.type === 'record') {
        options.push(...(contextMenuOptionsList[object.record.type] ?? []));
        if (isTracksProduct(layer.product.id)) {
            options.push(ContextMenuOption.properties);
        } else if (isRaimProduct(layer.product.id)) {
            options.push(ContextMenuOption.properties);
        } else if (layer.product.id === 0x600c || layer.product.id === 0x6086 || layer.product.id === 0x71) {
            // pireps
            options.push(ContextMenuOption.copyToClipboard);
        } else if (layer.palette === GDFPalletCode.tfr) {
            options.push(ContextMenuOption.properties);
        } else if (
            layer.palette === GDFPalletCode.airmets ||
            layer.palette === GDFPalletCode.sigmets ||
            layer.palette === GDFPalletCode.csigmets ||
            layer.palette === GDFPalletCode.wsi_fpg
        ) {
            options.push(ContextMenuOption.copyToClipboard);
        } else if (layer.palette === GDFPalletCode.ozone) {
            options.push(ContextMenuOption.copyToClipboard);
        } else if (layer.palette === GDFPalletCode.dsua) {
            options.push(ContextMenuOption.copyToClipboard);
        } else if (layer.product.id === 0x83) {
            // Delta turbulence plots
            options.push(ContextMenuOption.properties);
        }
        // Topical cyclone HYD
        else if (layer.product.id === 0x6089) {
            options.push(ContextMenuOption.properties);
        } else if (layer.palette === GDFPalletCode.watchwarn) {
            options.push(ContextMenuOption.properties);
        }

        //add fpm avoidance area option if integration enabled
        if (integrationInfo.enabled && integrationInfo.isFPM) {
            if (
                object.record.type === GDFRecordType.ccfp ||
                object.record.type === GDFRecordType.fca ||
                object.record.type === GDFRecordType.hotpoly ||
                object.record.type === GDFRecordType.hotpolyhi
            ) {
                options.push(ContextMenuOption.createFPMAvoidanceArea);
            }
        }
    } else if (object.type === 'subRecord') {
        if (object.record.type === GDFSubRecordType.wmoAshCloud2) {
            options.push(ContextMenuOption.createDrawingObject);
        }
    }
    return options;
}

function objectToDisplayName(layer: WeatherPickingInfoLayer, object: WeatherPickingInfoObject): string {
    if (object.type === 'raster') {
        return layer.product.name;
    }

    if (object.type === 'subRecord') {
        if (object.record.type === GDFSubRecordType.wmoAshCloud || object.record.type === GDFSubRecordType.wmoAshCloud2) {
            return 'Ash cloud';
        }
        return '???';
    }

    // NOTE: some layers pick only 1 record on purpose (e.g. Metar/Taf/Delays etc)
    // so technically they will never appear under cascading menu and don't need to be named by this function but that may change
    const record = object.record;
    switch (record.type) {
        case GDFRecordType.metar:
            return record.id;
        case GDFRecordType.hotpoly:
        case GDFRecordType.hotpolyhi:
            if (record.polyType === GDFHotPollyTypes.sua && !record.reduced) {
                return record.label;
            } else if (record.polyType === GDFHotPollyTypes.notam_tfr && !record.reduced) {
                return record.label;
            }
            if (!record.reduced) {
                return HotPolyTitle({ type: record.polyType, id: layer.product.id, direction: record.dir }) ?? '';
            }
            return GDFHotPollyTypes[record.polyType];
        case GDFRecordType.custom:
            return record.menu;
        case GDFRecordType.delay:
            return record.airportId;
        case GDFRecordType.diversion:
            return record.airportId;
        case GDFRecordType.taf:
            return record.id;
        case GDFRecordType.wmovolcano:
            return record.name;
        case GDFRecordType.wmohzdvol:
            return record.name;
        case GDFRecordType.wmocat:
            return 'Turbulence';
        case GDFRecordType.wmocloud:
            return 'Ash cloud';
        case GDFRecordType.ccfp:
            return 'TCF';
        case GDFRecordType.pirep:
            return record.text;
        case GDFRecordType.fca:
            return record.label || record.name;
        case GDFRecordType.pirep2:
            return (
                [
                    record.turbulenceType !== 0 ? 'Turbulence' : '',
                    record.icingSeverity !== null ? 'Icing' : '',
                    record.llws ? 'LLWS' : '',
                    record.skyCover !== null ? 'Sky & Weather' : '',
                    record.windSpeed !== null ? 'Winds' : '',
                    record.acType,
                    record.acid
                ]
                    .filter(Boolean)
                    .join(', ') || 'Pirep'
            );
        case GDFRecordType.iataturb:
            return 'IATA Turb';
        case GDFRecordType.wmovolcano2:
            return record.name;
    }

    return GDFRecordType[record.type] || '???';
}
